import React from "react";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import cx from "apps/website/utils/misc/cx";
import {
  Padding,
  paddingLeftMap,
  paddingRightMap,
  paddingXMap,
  paddingYMap,
} from "apps/website/maps/Spacing.map";
import { MaxWidth, maxWidthClassMap } from "apps/website/maps/Size.map";

import { iconSvgMap } from "../Icon/Icon.map";
import Icon from "../Icon/Icon";
import TextSubtitle from "../Text/TextSubtitle/TextSubtitle";
import TextBody from "../Text/TextBody/TextBody";
import { Color } from "../Text/Text.map";

const PADDING_WITH_ICON: { left: Padding; right: Padding } = { left: 2, right: 3 };
const PADDING_WITHOUT_ICON: { left: Padding; right: Padding } = { left: 3, right: 3 };

export const pillIconMap = {
  tick: iconSvgMap.tick,
  tickShort: iconSvgMap.tickShort,
  tickHeavy: iconSvgMap.tickHeavy,
  crossHeavy: iconSvgMap.crossHeavy,
};

export interface IPillProps {
  icon?: keyof typeof pillIconMap;
  iconClassName?: string;
  label: string;
  className?: string;
  styleOptions?: {
    bgColor?: Theme,
    textColor?: Color,
    paddingY?: Padding,
    paddingX?: Padding,
    paddingLeft?: Padding,
    paddingRight?: Padding,
    maxWidth?: MaxWidth,
    textDisplay?: "subtitle" | "body"
  };
}

const TEXT_DISPLAY_MAP = { body: TextBody, subtitle: TextSubtitle } as const;

export const Pill = ({ icon, iconClassName, label, className, styleOptions = {} }: IPillProps) => {
  const {
    bgColor = "default",
    textColor = "default",
    paddingY,
    paddingX,
    paddingLeft,
    paddingRight,
    maxWidth = "full",
    textDisplay = "subtitle",
  } = styleOptions;

  const defaultPadding = icon ? PADDING_WITH_ICON : PADDING_WITHOUT_ICON;
  const resolvedPaddingLeft = paddingLeft ?? paddingX ?? defaultPadding.left;
  const resolvedPaddingRight = paddingRight ?? paddingX ?? defaultPadding.right;
  const resolvedPaddingY = paddingY ? paddingYMap[paddingY] : "py-1.5";

  const pillClassName = cx(
    "inline-flex items-center gap-1 rounded-3xl",
    themeRootClassMap[bgColor],
    paddingXMap[paddingX ?? 0],
    resolvedPaddingY,
    paddingLeftMap[resolvedPaddingLeft],
    paddingRightMap[resolvedPaddingRight],
    maxWidthClassMap[maxWidth],
    className,
  );

  const TextComponent = TEXT_DISPLAY_MAP[textDisplay];

  return (
    <div data-component={Pill.name} className={pillClassName}>
      { icon && (
        <div className="flex items-center justify-center w-4 h-4 shrink-0">
          <Icon icon={icon} size="fit" className={iconClassName} />
        </div>
      ) }
      <TextComponent size={3} color={textColor} className="leading-none">
        { label }
      </TextComponent>
    </div>
  );
};
